// /admin/templates — operator console: vertical templates. The
// foundation every bot in an industry inherits. Editing a template changes
// every client in that vertical, so it's versioned + published on purpose.
// Data via the getVerticalTemplates() seam (mock).

import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'

import {
  createVerticalTemplate,
  getVerticalTemplates,
  type VerticalTemplate,
} from './templates-data.js'
import { DefinesList, VERTICAL_WRAP, VerticalIcon } from './templates-ui.js'
import {
  Banner,
  Button,
  Card,
  Chip,
  FormField,
  Input,
  Modal,
  PageHeader,
  Select,
  Spinner,
} from '../../design-system/index.js'
import { apiErrorMessage } from '../../lib/api-error.js'

function PlusIcon(): JSX.Element {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
      className="size-[15px]"
    >
      <path d="M12 5v14M5 12h14" />
    </svg>
  )
}

function TemplateCard({ tpl }: { tpl: VerticalTemplate }): JSX.Element {
  const navigate = useNavigate()
  return (
    <Card className="flex flex-col">
      <Card.Body className="flex-1">
        <div className="flex items-start gap-3">
          <span
            aria-hidden
            className={`grid size-11 flex-none place-items-center rounded-xl [&_svg]:size-[22px] ${VERTICAL_WRAP[tpl.vertical]}`}
          >
            <VerticalIcon vertical={tpl.vertical} />
          </span>
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <h3 className="text-[14px] font-bold text-text">{tpl.name}</h3>
              <Chip>{tpl.version}</Chip>
            </div>
            <p className="mt-0.5 text-[11px] text-text-3">{tpl.editedMeta}</p>
          </div>
          <div className="flex-none text-right">
            <div className="text-[18px] font-extrabold leading-none tabular-nums text-text">
              {tpl.botsInheriting}
            </div>
            <div className="mt-0.5 text-[10.5px] text-text-3">agentes</div>
          </div>
        </div>

        <div className="mt-4">
          <div className="mb-2 text-[10px] font-bold uppercase tracking-[0.06em] text-text-3">
            Define para sus agentes
          </div>
          <DefinesList defines={tpl.defines} />
        </div>
      </Card.Body>

      <Card.Foot className="justify-end">
        <Button
          variant="secondary"
          size="sm"
          onClick={() => void navigate(`/admin/templates/${tpl.id}`)}
        >
          Editar plantilla
        </Button>
      </Card.Foot>
    </Card>
  )
}

/**
 * Download the vertical templates as JSON.
 *
 * Deliberately a plain client-side download of what the page already has: no
 * endpoint, no server round-trip, nothing to keep in sync. What you export is
 * exactly what you are looking at.
 */
function exportTemplatesJson(templates: unknown): void {
  const blob = new Blob([JSON.stringify(templates, null, 2)], {
    type: 'application/json',
  })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `vysto-plantillas-${new Date().toISOString().slice(0, 10)}.json`
  a.click()
  URL.revokeObjectURL(url)
}

export function TemplatesPage(): JSX.Element {
  const navigate = useNavigate()
  const qc = useQueryClient()
  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'templates'],
    queryFn: getVerticalTemplates,
  })

  const [creating, setCreating] = useState(false)
  const [newName, setNewName] = useState('')
  const [newDesc, setNewDesc] = useState('')
  const [sourceId, setSourceId] = useState('')
  const [createBusy, setCreateBusy] = useState(false)
  const [createError, setCreateError] = useState('')

  async function submitCreate(): Promise<void> {
    setCreateBusy(true)
    setCreateError('')
    try {
      const created = await createVerticalTemplate({
        name: newName.trim(),
        ...(newDesc.trim() ? { description: newDesc.trim() } : {}),
        sourceId,
      })
      await qc.invalidateQueries({ queryKey: ['admin', 'templates'] })
      setCreating(false)
      void navigate(`/admin/templates/${created.id}`)
    } catch (err) {
      setCreateError(apiErrorMessage(err, 'No se pudo crear la plantilla.'))
    } finally {
      setCreateBusy(false)
    }
  }

  if (isLoading || !data) {
    return (
      <div data-testid="admin-templates" className="mx-auto w-full max-w-5xl py-10">
        <Spinner label="Cargando plantillas…" />
      </div>
    )
  }

  return (
    <div data-testid="admin-templates" className="mx-auto w-full max-w-5xl">
      <PageHeader
        title="Plantillas verticales"
        subtitle={`${data.length} industrias · ${data.reduce((n, t) => n + t.botsInheriting, 0)} agentes heredando`}
        actions={
          <>
            <Button variant="secondary" size="sm" onClick={() => exportTemplatesJson(data)}>
              Exportar JSON
            </Button>
            <Button
              size="sm"
              onClick={() => {
                setNewName('')
                setNewDesc('')
                setSourceId(data[0]?.id ?? '')
                setCreateError('')
                setCreating(true)
              }}
            >
              <PlusIcon />
              Nueva plantilla
            </Button>
          </>
        }
      />

      <div className="mt-5">
        <Banner tone="info">
          <b>Cómo funciona la herencia.</b> Cada agente pertenece a una vertical y hereda su
          plantilla: categorías de intención, reglas de emergencia, tono por defecto, habilidades
          base y normas de cumplimiento. La configuración del agente puede afinar los campos
          ajustables; los marcados como <b>no negociables</b> (por ejemplo, que el agente nunca
          afirme ser humano) se aplican siempre.
        </Banner>
      </div>

      <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-2">
        {data.map((tpl) => (
          <TemplateCard key={tpl.id} tpl={tpl} />
        ))}
      </div>

      <Modal
        open={creating}
        onClose={() => setCreating(false)}
        title="Nueva plantilla"
        subtitle="Parte de una plantilla existente y ajústala después. Hereda toda su configuración."
        footer={
          <>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setCreating(false)}
              disabled={createBusy}
            >
              Cancelar
            </Button>
            <Button
              size="sm"
              onClick={() => void submitCreate()}
              loading={createBusy}
              disabled={newName.trim() === '' || sourceId === ''}
            >
              Crear plantilla
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <FormField
            label="Partir de"
            htmlFor="tpl-source"
            helper="La nueva plantilla copia la configuración de esta y queda en su misma industria."
          >
            <Select
              value={sourceId}
              onChange={(e) => setSourceId(e.target.value)}
              options={data.map((t) => ({ value: t.id, label: `${t.name} (${t.version})` }))}
            />
          </FormField>
          <FormField label="Nombre de la plantilla" htmlFor="tpl-name">
            <Input
              id="tpl-name"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="p. ej. Hospital · Pediatría"
            />
          </FormField>
          <FormField label="Descripción" htmlFor="tpl-desc" required="optional">
            <Input
              id="tpl-desc"
              value={newDesc}
              onChange={(e) => setNewDesc(e.target.value)}
              placeholder="¿En qué se diferencia de la original?"
            />
          </FormField>
          {createError ? <p className="text-[12px] text-sens">{createError}</p> : null}
        </div>
      </Modal>
    </div>
  )
}
